import { Fragment, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Newspaper, RefreshCw, Plus, Loader2 } from "lucide-react";
import { getArticles, getHomeFeed, getCategories, apiCheckIn, getTopStory } from "@/lib/api";
import { categoryFromSlug, catColor, categorySlug, displayCat } from "@/lib/readoo";
import { CategoryChips } from "@/components/CategoryChips";
import { BreakingBanner } from "@/components/BreakingBanner";
import { NewsCard, HeroCard } from "@/components/NewsCard";
import { TopStories, CategoryTopStory } from "@/components/TopStories";
import { TopicOfDay } from "@/components/TopicOfDay";
import { WeatherAlert } from "@/components/WeatherAlert";
import { LocalNewsBox } from "@/components/LocalNewsBox";
import { RadarReadoo } from "@/components/engagement/RadarReadoo";
import { DailyBrief } from "@/components/engagement/DailyBrief";
import { DailyPoll } from "@/components/engagement/DailyPoll";
import { OnThisDay } from "@/components/engagement/OnThisDay";

import { SerieAScores } from "@/components/SerieAScores";
import { SerieAStandings } from "@/components/SerieAStandings";
import { SectionHeader } from "@/components/primitives";
import { Sidebar } from "@/components/Sidebar";
import { MySignCard } from "@/components/MySignCard";
import { CardSkeleton, HeroSkeleton, EmptyState } from "@/components/Skeletons";
import { AdSlot } from "@/components/Ads";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { CouponOfDayBox } from "@/components/CouponOfDay";
import { HomeSignupBand } from "@/components/VantaggiTeaser";

const PAGE_SIZE = 16;
const ENGAGE_AFTER = [<DailyBrief key="brief" />, <DailyPoll key="poll" />, <OnThisDay key="otd" />];

export default function Home() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => { getCategories().then((r) => setCategories(r.categories || [])).catch(() => {}); }, []);
  useEffect(() => { if (user) apiCheckIn().catch(() => {}); }, [user]);
  useEffect(() => { setActiveCategory(slug ? categoryFromSlug(slug, categories) : null); }, [slug, categories]);

  const seoTitle = activeCategory ? `Notizie di ${displayCat(activeCategory)}` : undefined;
  const seoDesc = activeCategory
    ? `Le ultime notizie di ${displayCat(activeCategory)} su Readoo: chiare e senza rumore.`
    : undefined;

  return (
    <div>
      <SEO title={seoTitle} description={seoDesc} />
      <BreakingBanner />
      <CategoryChips
        categories={categories}
        active={activeCategory}
        showForYou={!!user && (((user.interests?.length || 0) + (user.interest_tags?.length || 0)) > 0 || !!user.zodiac_sign)}
        showCreateFeed={!!user && ((user.interests?.length || 0) + (user.interest_tags?.length || 0)) === 0 && !user.zodiac_sign}
      />
      {!slug && <HomeSignupBand />}
      {slug ? (
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 min-w-0">
              <CategoryView category={activeCategory} slug={slug} categoriesReady={categories.length > 0} />
            </div>
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <Sidebar />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <HomeFeed />
      )}
    </div>
  );
}

// ---------- Home: featured + grouped-by-category sections (in the main column) ----------
function HomeFeed() {
  const [featured, setFeatured] = useState([]);
  const [top, setTop] = useState([]);
  const [sections, setSections] = useState([]);
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getHomeFeed()
      .then((r) => {
        if (!alive) return;
        setFeatured(r.featured || []);
        setTop(r.top_stories || []);
        setSections(r.sections || []);
        setTopic(r.topic || null);
      })
      .catch(() => { if (alive) { setFeatured([]); setSections([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <HeroSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">{Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      </div>
    );
  }

  const [hero, ...rest] = featured;
  if (!hero && sections.length === 0 && !topic && top.length === 0) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmptyState
          icon={Newspaper}
          title="Nessun articolo al momento"
          subtitle="Gli articoli vengono pubblicati in continuazione. Torna tra qualche minuto."
          action={<button onClick={() => window.location.reload()} className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand text-onBrand text-sm font-semibold hover:bg-brandSecondary transition-colors"><RefreshCw size={15} /> Aggiorna</button>}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Slider in evidenza a tutta larghezza: 1 hero + 3 card */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <WeatherAlert />
          <p data-testid="home-today-date" className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.18em] text-subtle -mb-3">
            {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" })}
          </p>
          <TopicOfDay topic={topic} />
          <TopStories items={top} />
          {top.length > 0 && hero && (
            <h2 data-testid="ultimissime-heading" className="font-serif text-xl sm:text-2xl font-semibold text-onSurface pt-4">Ultimissime</h2>
          )}
          {hero && <HeroCard article={hero} />}
          {rest.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {rest.map((a, i) => [
                <NewsCard key={a.article_id} article={a} index={i} />,
                i === 0 && <CouponOfDayBox key="cod" square className="sm:hidden" testid="cod-box-home-mobile" />,
              ])}
            </div>
          )}
          <CouponOfDayBox className="hidden sm:block" testid="cod-box-home" />
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <AdSlot placement="home_after_featured" ctx={{ page: "home" }} />
      </div>

      {/* Categorie + sidebar "Più letti" */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 min-w-0 space-y-12">
            <LocalNewsBox className="" testid="local-news-home" />
            {/* Prima il giornale: un modulo di engagement tra un blocco di notizie e l'altro */}
            <RadarReadoo />
            {sections.map(({ cat, items }, si) => (
              <Fragment key={cat}>
                <section data-testid={`home-section-${cat}`}>
                  <SectionHeader category={cat} to={`/categoria/${categorySlug(cat)}`} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {items.map((a, i) => <NewsCard key={a.article_id} article={a} index={i} compact />)}
                  </div>
                  {si === 0 && <AdSlot placement="between_posts" ctx={{ page: "home" }} />}
                </section>
                {ENGAGE_AFTER[si]}
              </Fragment>
            ))}
            {sections.length < ENGAGE_AFTER.length && ENGAGE_AFTER.slice(sections.length)}
          </div>
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Sidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Single category page with "load more" pagination ----------
function CategoryView({ category, slug, categoriesReady }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [top, setTop] = useState(null);
  const pagesRef = useRef(1);

  useEffect(() => {
    if (!categoriesReady || !category || category === "Oroscopo") { setTop(null); return; }
    let alive = true;
    setTop(null);
    getTopStory(category).then((r) => { if (alive) setTop(r.article || null); }).catch(() => {});
    return () => { alive = false; };
  }, [category, categoriesReady]);

  useEffect(() => {
    if (!categoriesReady) return;
    let alive = true;
    setLoading(true);
    setItems([]);
    setHasMore(false);
    pagesRef.current = 1;
    getArticles({ category: category || undefined, limit: PAGE_SIZE, skip: 0, fresh_only: false })
      .then((r) => {
        if (!alive) return;
        const list = r.articles || [];
        setItems(list);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, slug, categoriesReady]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      // Skip a PAGINE FISSE (0,16,32…): non dipende dal conteggio client post-dedup,
      // così il server restituisce sempre 16 elementi finché non finiscono.
      const skip = pagesRef.current * PAGE_SIZE;
      const r = await getArticles({ category: category || undefined, limit: PAGE_SIZE, skip, fresh_only: false });
      const list = r.articles || [];
      pagesRef.current += 1;
      setItems((prev) => {
        const seen = new Set(prev.map((a) => a.article_id));
        return [...prev, ...list.filter((a) => !seen.has(a.article_id))];
      });
      setHasMore(list.length >= PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  };

  // In griglia a 2 colonne mostra sempre un numero PARI finché ci sono altre pagine
  // (l'eventuale elemento dispari compare col batch successivo). Solo l'ultima pagina può restare dispari.
  const listed = top ? items.filter((a) => a.article_id !== top.article_id && (!top.cluster_key || a.cluster_key !== top.cluster_key)) : items;
  const display = hasMore && listed.length % 2 === 1 ? listed.slice(0, -1) : listed;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        {category && <span className="w-3 h-3 rounded-full" style={{ backgroundColor: catColor(category) }} />}
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-onSurface">{category ? displayCat(category) : "Categoria"}</h1>
      </div>

      {category === "Oroscopo" && <div className="mb-6"><MySignCard /></div>}
      {category === "Calcio" && <SerieAScores />}
      {category === "Calcio" && <SerieAStandings />}
      {!loading && <CategoryTopStory article={top} />}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">{Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Newspaper} title="Nessun articolo in questa categoria" subtitle="Riprova tra qualche minuto." />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {display.map((a, i) => [
              <NewsCard key={a.article_id} article={a} index={i % PAGE_SIZE} />,
              i === 1 && <CouponOfDayBox key="cod" className="sm:col-span-2" testid="cod-box-category" />,
            ])}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="load-more-button"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-hair bg-surfaceSecondary text-onSurface hover:border-brand/50 hover:text-brand disabled:opacity-60 transition-colors text-sm font-semibold"
              >
                {loadingMore ? <><Loader2 size={16} className="animate-spin" /> Caricamento…</> : <><Plus size={16} /> Più articoli</>}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
